import { FC, useMemo, useState } from 'react'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import Modal from '@common/Modal'
import { IcoRemove } from '@/utils/icons'
import Button from '@UI/New/Button'
import classNames from 'classnames'
import { ITimetableReportItem } from '@pages/Journal2/components/Tabs/KTPJournal'
import SelectField from '@pages/Admin/CourseSettings/components/SelectField/SelectField'
import TextAreaField from '@pages/Admin/CourseSettings/components/TextAreaField/TextAreaField'
import cn from './index.module.css'
import TextField from '@pages/Admin/CourseSettings/components/TextField/TextField'
import { useAddAlert } from '@/hooks/useAddAlert'
import { changeLessonDuration, changeLessonSalary, changeLessonStatus } from './utils/api'
import TooltipWrapper from '@pages/Admin/CourseSettings/components/TooltipWrapper/TooltipWrapper'
import { InfoCircleOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import {
  isLessonStatusCommentRequired,
  shouldSaveLessonSalary,
} from '@pages/Main/LessonReports/helpers/getLessonStatusOptions'

type EditableLessonReport = ITimetableReportItem & {
  salary?: number | null
  sallary?: number | null
}

interface IChangeLessonReportStatusModal {
  open: boolean
  report: EditableLessonReport | null
  onSuccess: () => void
  onDismiss: () => void
}

const ChangeLessonReportStatusModal: FC<IChangeLessonReportStatusModal> = (
  props,
) => {
  return (
    <Modal
      active={props.open}
      className={classNames(mclasses.modalWrapper, cn.modalWrapper) as any}
      setActive={() => props.onDismiss()}
    >
      <Body {...props} />
    </Modal>
  )
}

const statusOptions = [
  { label: 'Проведено', value: '5' },
  { label: 'Отмена учеником', value: '10' },
  { label: 'Отмена педагогом', value: '11' },
  { label: 'Платная отмена', value: '12' },
]

const Body: FC<IChangeLessonReportStatusModal> = (props) => {
  const addAlert = useAddAlert()
  const [loading, setLoading] = useState(false)
  const [salaryChanged, setSalaryChanged] = useState(false)
  const role = useSelector((store: any) => store.profile?.profile?.role)
  const canEditSalary = !['seminarian', 'senior-seminarian'].includes(role)
  const initialSalary = props.report?.salary ?? props.report?.sallary ?? 0
  const [form, setForm] = useState({
    status: String(
      statusOptions.some((item) => item.value === String(props.report?.status))
        ? props.report?.status
        : '5',
    ),
    comment: String(props.report?.comment ?? ''),
    duration: props.report?.duration ?? 0,
    salary: Number(initialSalary ?? 0),
  })

  const options = useMemo(() => {
    return statusOptions.filter((item) => {
      if (props.report?.conference_type === 'introduction') {
        return item.value !== '12'
      }
      if (props.report?.conference_type === 'group') {
        return item.value !== '10'
      }
      return true
    })
  }, [props.report])

  const onSave = async () => {
    setLoading(true)
    await saveHandle()
    setLoading(false)
  }

  const saveHandle = async () => {
    const isCommentRequired = isLessonStatusCommentRequired(
      form.status,
      props.report?.conference_type,
    )

    if (isCommentRequired && !form.comment.trim()) {
      addAlert({ title: 'Нужно заполнить комментарий' })
      return
    }

    const resChangeStatus = await changeLessonStatus(
      props.report!.id,
      form.comment,
      form.status,
    )

    if (!resChangeStatus) {
      addAlert({
        title: 'Ошибка',
        text: 'Попробуйте ещё раз или обратитесь к администратору',
        type: 'error',
      })
      return
    }

    const resChangeDuration = await changeLessonDuration(
      props.report!.id,
      form.duration,
    )

    if (!resChangeDuration) {
      addAlert({
        title: 'Ошибка',
        text: 'Попробуйте ещё раз или обратитесь к администратору',
        type: 'error',
      })
      return
    }

    if (shouldSaveLessonSalary(
      canEditSalary,
      props.report?.conference_type,
      salaryChanged,
    )) {
      const resChangeSalary = await changeLessonSalary(props.report!.id, form.salary)
      if (!resChangeSalary) {
        addAlert({
          title: 'Ошибка',
          text: 'Не удалось обновить ЗП за занятие',
          type: 'error',
        })
        return
      }
    }

    addAlert({ title: 'Данные занятия обновлены' })
    props.onSuccess()
  }

  return (
    <div className={mclasses.modalContainer}>
      <div className={mclasses.modalHeader}>
        <div className={mclasses.modalTitleContainer}>
          <span className={mclasses.modalHeaderTitle}>Редактировать отчет о занятии</span>
        </div>
        <div className={mclasses.closeIcon} onClick={props.onDismiss}>
          <IcoRemove />
        </div>
      </div>
      <div className={mclasses.formWrapper}>
        <SelectField
          className={cn.select}
          label="Статус"
          value={form.status}
          options={options}
          onChange={(value) => {
            if (typeof value === 'string') {
              setForm((prev) => ({ ...prev, status: value }))
            }
          }}
        />
        <TextAreaField
          className={cn.textarea}
          label={'Комментарий'}
          placeholder="комментарий"
          value={form.comment}
          onChange={(value) => {
            setForm((prev) => ({ ...prev, comment: value.target.value }))
          }}
          autoSize={{ minRows: 3, maxRows: 6 }}
        />
        <TextField
          label="Длительность"
          className={cn.textarea}
          value={String(form.duration)}
          onChange={(value) => {
            if (Number.isNaN(Number(value))) return
            setForm((prev) => ({ ...prev, duration: Number(value) }))
          }}
        />
        <TextField
          label="ЗП педагога"
          className={cn.textarea}
          value={String(form.salary)}
          disabled={!canEditSalary}
          extraContent={
            <TooltipWrapper title="Здесь вы можете изменить зарплату педагога за конкретное занятие. Важно: вы не меняете структуру формирования зарплаты по данному предмету и данной группе у педагога, а лишь меняете начисленную сумму за одно конкретное занятие.">
              <span style={{ display: 'inline-flex', cursor: 'pointer' }}>
                <InfoCircleOutlined onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
              </span>
            </TooltipWrapper>
          }
          description={
            canEditSalary
              ? 'Можно изменить сумму начисления только за это занятие.'
              : 'Педагог видит расчетную сумму, но не может изменить себе ЗП.'
          }
          onChange={(value) => {
            if (Number.isNaN(Number(value))) return
            setSalaryChanged(true)
            setForm((prev) => ({ ...prev, salary: Number(value) }))
          }}
        />
      </div>
      <div className={mclasses.actions}>
        <Button color="gray" onClick={props.onDismiss}>
          Закрыть
        </Button>
        <Button color="primary" onClick={onSave} disabled={loading} isLoading={loading}>
          Сохранить
        </Button>
      </div>
    </div>
  )
}

export default ChangeLessonReportStatusModal
